import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChatCircleDots,
  PlugsConnected,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AppHeader } from "@/components/AppHeader";
import { api, API, formatApiErrorDetail } from "@/lib/api";

export default function TwilioSettings() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    account_sid: "",
    auth_token: "",
    from_number: "",
    tpl_on_my_way: "",
    tpl_job_complete: "",
    review_url: "",
    notify_appt_reminders: true,
    notify_reschedule_alerts: true,
    notify_crew_reminders: true,
    notify_office_alerts: true,
    quick_replies: [],
  });
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(null);
  const [consentPhone, setConsentPhone] = useState("");
  const [replyStatus, setReplyStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const loadConsent = () =>
    api
      .get("/sms/consent")
      .then(({ data }) => setConsent(data))
      .catch(() => setConsent(null));

  const loadReplyStatus = () =>
    api
      .get("/sms/reply-status")
      .then(({ data }) => setReplyStatus(data))
      .catch(() => setReplyStatus(null));

  const connectReplies = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/sms/connect-replies");
      toast.success(`Replies connected for ${data.number}`);
      await loadReplyStatus();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not connect replies",
      );
    }
    setConnecting(false);
  };

  const setConsentFor = async (optedIn) => {
    if (!consentPhone.trim()) return toast.error("Type a phone number first");
    try {
      await api.post("/sms/consent", {
        phone: consentPhone.trim(),
        opted_in: optedIn,
      });
      toast.success(optedIn ? "Number opted in" : "Number opted out");
      setConsentPhone("");
      await loadConsent();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save that number",
      );
    }
  };

  const load = () =>
    api.get("/sms/settings").then(({ data }) => {
      setInfo(data);
      setForm({
        account_sid: data.account_sid || "",
        auth_token: "",
        from_number: data.from_number || "",
        tpl_on_my_way: data.tpl_on_my_way || "",
        tpl_job_complete: data.tpl_job_complete || "",
        review_url: data.review_url || "",
        notify_appt_reminders: data.notify_appt_reminders !== false,
        notify_reschedule_alerts: data.notify_reschedule_alerts !== false,
        notify_crew_reminders: data.notify_crew_reminders !== false,
        notify_office_alerts: data.notify_office_alerts !== false,
        quick_replies: data.quick_replies || [],
      });
    });

  useEffect(() => {
    load().catch(() => toast.error("Could not load texting settings"));
    loadConsent();
    loadReplyStatus();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/sms/settings", form);
      toast.success("Texting settings saved");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sms/settings/test");
      toast.success(`Connected to Twilio · sending from ${data.from_number}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Connection failed",
      );
    }
    setBusy(false);
  };

  const field = (key, label, placeholder, type = "text") => (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-widest text-zinc-400">
        {label}
      </Label>
      <Input
        data-testid={`twilio-${key.replace(/_/g, "-")}-input`}
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="h-12 border-zinc-800 bg-zinc-900"
      />
    </div>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Settings / Texting" />
      <main className="relative z-10 mx-auto max-w-2xl px-4 pb-28 pt-6">
        <button
          onClick={() => navigate("/settings")}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Settings
        </button>
        <div className="text-xs uppercase tracking-widest text-zinc-600">
          Integrations
        </div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Texting (Twilio)
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Credentials used to text customers and crew from account and job pages
        </p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="twilio-actions">
          <Button
            data-testid="twilio-save-button"
            disabled={busy}
            onClick={save}
            className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            {busy ? "Saving…" : "Save settings"}
          </Button>
          <Button
            data-testid="twilio-test-button"
            variant="outline"
            disabled={busy}
            onClick={test}
            className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
          >
            <PlugsConnected size={15} weight="bold" className="mr-2" /> Test
            connection
          </Button>
        </div>

        <div className="mt-6 space-y-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          <div className="flex items-center gap-2">
            <ChatCircleDots
              size={16}
              weight="bold"
              className="text-indigo-300"
            />
            <span
              data-testid="twilio-status"
              className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                info?.auth_token_set && info?.from_number
                  ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-400"
                  : "border-zinc-700 text-zinc-400"
              }`}
            >
              {info?.auth_token_set && info?.from_number
                ? "Texting on"
                : "Not set up"}
            </span>
            {info?.updated_by && (
              <span className="text-[11px] text-zinc-600">
                updated by {info.updated_by} ·{" "}
                {(info.updated_at || "").slice(0, 10)}
              </span>
            )}
          </div>
          {field("account_sid", "Account SID", "AC…")}
          {field(
            "auth_token",
            "Auth Token",
            info?.auth_token_set
              ? `Saved (${info.auth_token_hint}) — type to replace`
              : "Your auth token",
            "password",
          )}
          {field("from_number", "Sending number", "+13145550123")}
          <p className="text-xs text-zinc-500">
            Find these at console.twilio.com — SID and token on the dashboard,
            the number under Phone Numbers → Active numbers.
          </p>
        </div>

        <div
          className="mt-4 space-y-4 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="twilio-templates"
        >
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Automatic text wording
          </div>
          <p className="text-xs text-zinc-500">
            Sent automatically to customers. Tokens you can use:{" "}
            <code className="text-indigo-300">{"{first}"}</code>{" "}
            <code className="text-indigo-300">{"{name}"}</code>{" "}
            <code className="text-indigo-300">{"{shop}"}</code>{" "}
            <code className="text-indigo-300">{"{stage}"}</code>{" "}
            <code className="text-indigo-300">{"{project}"}</code>{" "}
            <code className="text-indigo-300">{"{code}"}</code>{" "}
            <code className="text-indigo-300">{"{review}"}</code>
          </p>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Google review link
            </Label>
            <Input
              data-testid="twilio-review-url-input"
              value={form.review_url}
              onChange={(e) => setForm({ ...form, review_url: e.target.value })}
              placeholder="https://g.page/r/…"
              className="h-12 border-zinc-800 bg-zinc-900"
            />
            <p className="text-[11px] text-zinc-600">
              Paste your Google &quot;review us&quot; short link. The{" "}
              <code className="text-indigo-300">{"{review}"}</code> token then adds a
              one-tap review ask — it&apos;s already in the default install-complete text.
            </p>
          </div>
          <div
            className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="twilio-auto-notify"
          >
            <div className="space-y-1">
              <Label className="text-sm font-medium text-zinc-100">
                Automatic notification texts
              </Label>
              <p className="text-[11px] text-zinc-500">
                Turn any kind off to stop it auto-sending — you can still text
                customers by hand with the Text buttons.
              </p>
            </div>
            {[
              {
                key: "notify_appt_reminders",
                title: "Appointment reminders",
                note: "Reminds the customer before a booked template/install date.",
              },
              {
                key: "notify_reschedule_alerts",
                title: "Reschedule alerts",
                note: "Texts the customer the new date when a booked date moves.",
              },
              {
                key: "notify_crew_reminders",
                title: "Crew task reminders",
                note: "Reminds your crew of the day's assigned jobs.",
              },
              {
                key: "notify_office_alerts",
                title: "Office alert texts",
                note: "Texts to office staff — quote accepted, payments, order-needed and note alerts.",
              },
            ].map((row) => (
              <div
                key={row.key}
                className="flex items-start justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-950 p-3"
              >
                <div className="space-y-0.5">
                  <div className="text-sm text-zinc-100">{row.title}</div>
                  <p className="text-[11px] text-zinc-500">{row.note}</p>
                </div>
                <Switch
                  data-testid={`twilio-${row.key.replace(/_/g, "-")}-switch`}
                  checked={form[row.key]}
                  onCheckedChange={(v) => setForm({ ...form, [row.key]: v })}
                />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              &quot;On my way&quot; text
            </Label>
            <textarea
              data-testid="twilio-tpl-on-my-way"
              value={form.tpl_on_my_way}
              onChange={(e) => setForm({ ...form, tpl_on_my_way: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 p-3 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              &quot;Install complete&quot; text
            </Label>
            <textarea
              data-testid="twilio-tpl-job-complete"
              value={form.tpl_job_complete}
              onChange={(e) => setForm({ ...form, tpl_job_complete: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 p-3 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <p className="text-[11px] text-zinc-600">
            Leave a box empty to use the built-in default wording.
          </p>
        </div>

        <div
          className="mt-4 space-y-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="twilio-quick-replies"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Quick replies
            </div>
            <button
              type="button"
              data-testid="twilio-quick-add"
              onClick={() =>
                setForm({
                  ...form,
                  quick_replies: [...(form.quick_replies || []), { label: "", body: "" }],
                })
              }
              className="rounded-full border border-indigo-500/40 bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-200 hover:bg-indigo-500/20"
            >
              + Add
            </button>
          </div>
          <p className="text-xs text-zinc-500">
            One-tap canned texts crews can send from a job or the inbox. Tokens:{" "}
            <code className="text-indigo-300">{"{name}"}</code>{" "}
            <code className="text-indigo-300">{"{shop}"}</code>
          </p>
          {(form.quick_replies || []).length === 0 && (
            <p className="text-[11px] text-zinc-600">
              None set — the built-in four are used.
            </p>
          )}
          {(form.quick_replies || []).map((q, i) => (
            <div
              key={i}
              data-testid={`twilio-quick-row-${i}`}
              className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3"
            >
              <div className="flex items-center gap-2">
                <Input
                  data-testid={`twilio-quick-label-${i}`}
                  value={q.label}
                  onChange={(e) => {
                    const next = [...form.quick_replies];
                    next[i] = { ...next[i], label: e.target.value };
                    setForm({ ...form, quick_replies: next });
                  }}
                  placeholder="Button label (e.g. On our way)"
                  className="h-10 flex-1 border-zinc-800 bg-zinc-900"
                />
                <button
                  type="button"
                  data-testid={`twilio-quick-remove-${i}`}
                  onClick={() =>
                    setForm({
                      ...form,
                      quick_replies: form.quick_replies.filter((_, j) => j !== i),
                    })
                  }
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 hover:text-red-400"
                  aria-label="Remove quick reply"
                >
                  ✕
                </button>
              </div>
              <textarea
                data-testid={`twilio-quick-body-${i}`}
                value={q.body}
                onChange={(e) => {
                  const next = [...form.quick_replies];
                  next[i] = { ...next[i], body: e.target.value };
                  setForm({ ...form, quick_replies: next });
                }}
                rows={2}
                placeholder="Message text sent to the customer"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 p-2 text-sm text-zinc-100 focus:border-indigo-500 focus:outline-none"
              />
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Texting consent
            </div>
            <span
              data-testid="consent-counts"
              className="font-mono-data text-[11px] text-zinc-500"
            >
              {consent?.opted_in ?? 0} opted in · {consent?.opted_out ?? 0}{" "}
              opted out
            </span>
          </div>
          <p className="mt-2 text-sm text-zinc-400">
            Numbers that ticked the box on the website, or replied STOP. We
            never text a number that has opted out.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Input
              data-testid="consent-phone-input"
              value={consentPhone}
              onChange={(e) => setConsentPhone(e.target.value)}
              placeholder="+13145550123"
              className="h-11 w-56 border-zinc-800 bg-zinc-900"
            />
            <Button
              data-testid="consent-opt-in"
              variant="outline"
              onClick={() => setConsentFor(true)}
              className="h-11 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest hover:bg-zinc-800"
            >
              Opt in
            </Button>
            <Button
              data-testid="consent-opt-out"
              variant="outline"
              onClick={() => setConsentFor(false)}
              className="h-11 border-zinc-700 bg-transparent text-[11px] uppercase tracking-widest hover:bg-zinc-800"
            >
              Opt out
            </Button>
          </div>
          {(consent?.items || []).length > 0 && (
            <div
              className="mt-4 divide-y divide-zinc-800"
              data-testid="consent-list"
            >
              {(consent.items || []).slice(0, 12).map((c) => (
                <div
                  key={c.id}
                  data-testid={`consent-row-${c.phone}`}
                  className="flex flex-wrap items-center gap-3 py-2.5 text-sm"
                >
                  <span className="font-mono-data text-zinc-300">
                    {c.phone}
                  </span>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                      c.opted_in
                        ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-400"
                        : "border-red-500/25 bg-red-500/10 text-red-300"
                    }`}
                  >
                    {c.opted_in ? "Opted in" : "Opted out"}
                  </span>
                  {c.name && <span className="text-zinc-500">{c.name}</span>}
                  <span className="ml-auto text-[11px] text-zinc-600">
                    {c.source || ""} · {(c.updated_at || "").slice(0, 10)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-4">
          <div className="flex items-center gap-2">
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Reply webhook
            </div>
            {replyStatus && (
              <span
                data-testid="twilio-reply-status"
                className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                  replyStatus.connected
                    ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-400"
                    : "border-amber-500/25 bg-amber-500/10 text-amber-300"
                }`}
              >
                {replyStatus.connected ? "Replies on" : "Not receiving replies"}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-zinc-400">
            Customer replies only show up here once your number&apos;s &quot;A
            message comes in&quot; webhook points at this app. Tap below to set
            it automatically, or paste the URL into Twilio yourself (POST).
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              data-testid="twilio-connect-replies"
              disabled={connecting}
              onClick={connectReplies}
              className="h-11 bg-indigo-500 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <PlugsConnected size={15} weight="bold" className="mr-2" />
              {connecting ? "Connecting…" : "Connect replies automatically"}
            </Button>
            {replyStatus?.detail && (
              <span className="text-[11px] text-amber-300/80">
                {replyStatus.detail}
              </span>
            )}
          </div>
          <code
            data-testid="twilio-webhook-url"
            className="font-mono-data mt-3 block break-all rounded-md border border-zinc-800 bg-zinc-900 p-3 text-xs text-indigo-300"
          >
            {replyStatus?.webhook_url || `${API}/sms/inbound`}
          </code>
          {replyStatus && !replyStatus.connected && replyStatus.current_sms_url ? (
            <p className="mt-2 text-[11px] text-zinc-600">
              Twilio currently sends replies to{" "}
              <span className="break-all text-zinc-400">
                {replyStatus.current_sms_url}
              </span>
            </p>
          ) : null}
        </div>
      </main>
    </div>
  );
}
